import GoogleMapReact from 'google-map-react';
import React, { useEffect, useRef, useState } from 'react';
import useSupercluster from 'use-supercluster';

import getGoogleMapsKey from '$utilities/google-maps-key';
import IconAdd from '$partials/icons/icon-add';
import IconSubtract from '$partials/icons/icon-subtract';
import PropertyListingItem from '$partials/items/property-listing-item';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/listings.d.ts').PropertyMapMarker}
 */
const PropertyMapMarker = ({
    count,
    isHighlight,
    isMuted,
    label,
    onClick
}) => {
    const size = (isHighlight || count) ? 28 : 22;

    return (
        <div className='w-[28px] h-[28px] flex items-center justify-center translate-x-[-50%] translate-y-[-50%]'>
            <button
                aria-label={label}
                className={`${isMuted ? 'bg-[#A2A9CB]' : 'bg-brand-500'} rounded-[50%] text-grey-grey-0 type-style-eyebrow`}
                onClick={onClick}
                style={{ width: `${size}px`, height: `${size}px`, lineHeight: `${size}px` }}
                type='button'>
                {count}
            </button>
        </div>
    );
};

/**
 * @type {import('$types/components/partials/listings.d.ts').PropertyMap}
 */
export const PropertyMap = ({
    defaultBounds,
    defaultCentre,
    highlight,
    loading,
    onHighlightChange,
    onSearch,
    onZoomChange,
    properties,
    zoom,
}) => {
    const [bounds, setBounds] = useState(defaultBounds);
    const [showSearch, setShowSearch] = useState(false);

    /** @type {React.MutableRefObject<google.maps.Map>} */
    const mapRef = useRef();
    /** @type {React.MutableRefObject<google.maps>} */
    const mapsRef = useRef();
    /** @type {React.MutableRefObject<google.maps.LatLngBounds>} */
    const autoBoundsRef = useRef();
    /** @type {React.MutableRefObject<ReturnType<typeof setTimeout>>} */
    const fitBoundsTimeoutRef = useRef();
    /** @type {React.MutableRefObject<ReturnType<typeof setTimeout>>} */
    const onChangeTimeoutRef = useRef();

    /** @type {import('supercluster').PointFeature<any>[]} */
    const points = properties?.filter(
        (property) => Boolean(property.coordinates && property.isOffMarket === false)
    ).map(
        (property) => {
            return {
                type: 'Feature',
                properties: { propertyId: property.id, propertyTitle: property.title },
                geometry: { type: 'Point', coordinates: [property.coordinates.lng, property.coordinates.lat] }
            };
        }
    );

    // Cluster points
    const { clusters, supercluster } = useSupercluster({
        points,
        bounds: [
            bounds?.nw.lng,
            bounds?.se.lat,
            bounds?.se.lng,
            bounds?.nw.lat
        ],
        zoom
    });

    /**
     * Open a cluster
     * @type {(cluster: import('supercluster').ClusterFeature<any>) => void}
     */
    const handleClusterClick = (cluster) => {
        if (mapRef.current) {
            const expansionZoom = supercluster.getClusterExpansionZoom(Number(cluster.id));
            mapRef.current.setZoom(expansionZoom);
            mapRef.current.panTo({ lat: cluster.geometry.coordinates[1], lng: cluster.geometry.coordinates[0] });
        }
    };

    /**
     * Set filter bounds to current map area
     */
    const handleSearch = () => {
        onHighlightChange(null);
        onSearch(bounds);
    };

    /**
     * Zoom in map
     */
    const handleZoomIn = () => {
        if (mapRef.current) {
            mapRef.current.setZoom(Math.ceil(zoom + 1));
        }
    };

    /**
     * Zoom out map
     */
    const handleZoomOut = () => {
        if (mapRef.current) {
            mapRef.current.setZoom(Math.floor(zoom - 1));
        }
    };

    /**
     * Fit map to default bounds, or otherwise to all available points
     */
    const fitBounds = async () => {
        if (mapRef.current && mapsRef.current) {
            /** @type {google.maps.LatLngBounds} */
            let latLngBounds;

            if (defaultBounds) {
                const { spherical } = await window.google.maps.importLibrary('geometry');

                // Expand bounds
                const nw = defaultBounds?.distance ?
                    spherical.computeOffset(defaultBounds.nw, defaultBounds?.distance, 315) :
                    new mapsRef.current.LatLng(defaultBounds.nw);
                const se = defaultBounds?.distance ?
                    spherical.computeOffset(defaultBounds.se, defaultBounds?.distance, 135) :
                    new mapsRef.current.LatLng(defaultBounds.se);

                latLngBounds = new mapsRef.current.LatLngBounds(nw, se);
                mapRef.current.fitBounds(latLngBounds, 0);
            } else {
                latLngBounds = new mapsRef.current.LatLngBounds();
                points.forEach((point) => {
                    const latLng = new mapsRef.current.LatLng(point.geometry.coordinates[1], point.geometry.coordinates[0]);
                    latLngBounds.extend(latLng);
                });
                mapRef.current.fitBounds(latLngBounds);

                // Prevent zooming in too far
                const minAutoZoom = 12;
                const currentZoom = mapRef.current.getZoom();
                mapRef.current.setZoom(currentZoom > minAutoZoom ? minAutoZoom : currentZoom);
            }

            // Calculate final bounds after animation has settled
            if (fitBoundsTimeoutRef.current) { clearTimeout(fitBoundsTimeoutRef.current); }
            fitBoundsTimeoutRef.current = setTimeout(() => {
                autoBoundsRef.current = mapRef.current.getBounds();
            }, 300);
        }
    };

    // Automatically update map bounds when listing filters are updated and no explicit filter bounds are set
    useEffect(() => {
        if (!loading) {
            fitBounds();
        }
    }, [defaultBounds, loading]);

    return (
        <div className={`${loading ? 'opacity-30 pointer-events-none ' : ''}absolute top-0 bottom-0 left-0 right-0`}>
            <GoogleMapReact
                yesIWantToUseGoogleMapApiInternals
                bootstrapURLKeys={{
                    key: getGoogleMapsKey(),
                }}
                defaultCenter={defaultCentre}
                defaultZoom={10}
                onChange={({ zoom: newZoom, bounds: newBounds }) => {
                    if (mapRef.current && mapsRef.current) {
                        onZoomChange(newZoom);
                        setBounds(newBounds);

                        // Check for manual position after animation has settled
                        if (onChangeTimeoutRef.current) { clearTimeout(onChangeTimeoutRef.current); }
                        onChangeTimeoutRef.current = setTimeout(() => {
                            setShowSearch(!autoBoundsRef.current || !mapRef.current.getBounds().equals(autoBoundsRef.current));
                        }, 300);
                    }
                }}
                onClick={() => onHighlightChange(null)}
                onGoogleApiLoaded={({ map, maps }) => {
                    mapRef.current = map;
                    mapsRef.current = maps;
                    fitBounds();
                }}
                options={{
                    mapId: 'ca9052da21cdf8f0b6ba14d4',
                    disableDefaultUI: true,
                    keyboardShortcuts: false,
                    gestureHandling: 'greedy',
                }}>

                {/* Markers */}
                {clusters.map((cluster, i) => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const {
                        cluster: isCluster,
                        point_count: pointCount
                    } = cluster.properties;

                    const isHighlight = Boolean(!isCluster && cluster.properties.propertyId === highlight);

                    return (
                        <PropertyMapMarker
                            count={pointCount}
                            isCluster={isCluster}
                            isHighlight={isHighlight}
                            isMuted={Boolean(!isHighlight && highlight)}
                            key={`cluster-${cluster.id}-${i}`}
                            label={isCluster ? `Select cluster of ${pointCount} propert${pointCount !== 1 ? 'ies' : 'y'}` : 'Select property'}
                            lat={latitude}
                            lng={longitude}
                            onClick={isCluster ? () => handleClusterClick(cluster) : () => onHighlightChange(cluster.properties.propertyId)} />
                    );
                })}
            </GoogleMapReact>

            {/* Zoom controls */}
            <div className='absolute bottom-0 right-0 text-center pointer-events-none z-10 p-16 flex gap-8'>
                <button
                    aria-label='Zoom out'
                    className='pointer-events-auto button-primary-filled w-[32px] h-[32px] !p-0'
                    onClick={handleZoomOut}
                    type='button'>
                    <IconSubtract />
                </button>

                <button
                    aria-label='Zoom in'
                    className='pointer-events-auto button-primary-filled w-[32px] h-[32px] !p-0'
                    onClick={handleZoomIn}
                    type='button'>
                    <IconAdd />
                </button>
            </div>

            {/* Search button */}
            {(!loading && showSearch) &&
                <div className='absolute top-0 left-0 right-0 text-center pointer-events-none z-10 p-12'>
                    <button
                        className='pointer-events-auto button-primary-filled type-style-eyebrow flex items-center !gap-8 h-[32px]'
                        onClick={() => {
                            handleSearch();
                            setShowSearch(false);
                        }}
                        type='button'>
                        Search this area
                    </button>
                </div>
            }

            {/* No results */}
            {(!loading && !showSearch && properties.length <= 0) &&
                <div className='absolute top-0 left-0 right-0 text-center pointer-events-none z-10 p-12'>
                    <div className='button-primary-filled type-style-eyebrow flex items-center !gap-8 h-[32px]'>
                        No results
                    </div>
                </div>
            }
        </div>
    );
};

/**
 * @type {import('$types/components/partials/listings.d.ts').PropertyMapView}
 */
export const PropertyMapView = ({
    defaultBounds,
    defaultCentre,
    highlight,
    listing,
    onHighlightChange,
    onSearch: _onSearch,
    onZoomChange,
    zoom,
}) => {
    /** @type {React.LegacyRef<HTMLDivElement>} */
    const containerRef = useRef();

    // Scroll highlighted property into view
    useEffect(() => {
        if (highlight) {
            const highlightItem = document.querySelector(`[data-property-id="${highlight}"]`);
            if (highlightItem) {
                highlightItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [highlight]);

    /** @type {(bounds: import('google-map-react').Bounds) => void} */
    const onSearch = (bounds) => {
        _onSearch(bounds);
    };

    return (
        <div
            className='layout-container sm:px-layout-outer min-h-[85svh] sm:min-h-[100svh]'
            ref={containerRef}>
            <div className='py-16 md:py-24 relative'>
                <div className='w-full sm:absolute sm:top-0 sm:right-0 sm:bottom-0 sm:w-8/12 md:w-1/2'>
                    <div className='sm:sticky sm:top-[58px] sm:py-16 sm:pl-16 md:py-24 md:pl-24 h-[85svh] sm:h-[calc(100svh-58px)]'>
                        <div className='relative h-full'>
                            <PropertyMap
                                defaultBounds={defaultBounds}
                                defaultCentre={defaultCentre}
                                highlight={highlight}
                                loading={listing.loading}
                                onHighlightChange={onHighlightChange}
                                onSearch={onSearch}
                                onZoomChange={onZoomChange}
                                properties={listing.items?.map((/** @type {{ node: object }} */ edge) => edge?.node)}
                                zoom={zoom} />
                        </div>
                    </div>
                </div>

                <div className='hidden sm:block w-4/12 md:w-1/2 sm:pr-16 md:pr-24'>
                    <div className='sm:-mx-16 md:-mx-24'>
                        <div
                            aria-busy={listing.loading}
                            aria-label='Properties'
                            className={`grid grid-cols-1 md:grid-cols-2 gap-y-40${(listing.loading && listing.page === 1) ? ' pointer-events-none opacity-50' : ''}`}
                            role='feed'>
                            {listing.items?.length > 0 ? listing.items.map(
                                /** @type {(edge: { node: import("$types/generated/cms-graphql.d.ts").SalePropertyListingItemFragment | import("$types/generated/cms-graphql.d.ts").RentalPropertyListingItemFragment }, i: number) => any} */
                                (edge, i) => {
                                    const { node } = edge;

                                    return (
                                        <article
                                            className={`sm:px-16 md:px-24 md:border-l md:border-l-tint-dark ${i % 2 === 0 ? 'md:border-opacity-0' : 'md:border-opacity-20'}`}
                                            data-property-id={node.id}
                                            key={`${i}-${node.id}`}>
                                            <PropertyListingItem
                                                {...node}
                                                isHighlight={node.id === highlight} />
                                        </article>
                                    );
                                }
                            ) : (
                                <div className='sm:px-16 md:px-24 py-56 type-style-3'>
                                    No results
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default PropertyMapView;
